import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, Card, Btn, Alert, DataTable, StandardModal,
  StatusBadge, FilterPills, KPICard, KPIGrid,
  colors, spacing, typography, radius, shadows,
  inputStyle, textareaStyle, selectStyle,
} from '../design-system';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_COLORS = {
  scheduled: colors.primary,
  in_progress: '#f59e0b',
  completed: '#10b981',
  cancelled: '#94a3b8',
};

function CheckItem({ item, index, sessionId, onUpdate }) {
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    setSaving(true);
    try {
      const updated = { ...item, completed: !item.completed };
      await axiosClient.patch(`/remote-audit/${sessionId}/checklist`, { index, completed: !item.completed });
      onUpdate(index, updated);
    } catch {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing.md,
        padding: `${spacing.sm} 0`,
        borderBottom: `1px solid ${colors.border}`,
        opacity: saving ? 0.6 : 1,
        transition: 'opacity 150ms',
      }}
    >
      <button
        onClick={toggle}
        disabled={saving}
        style={{
          flexShrink: 0,
          marginTop: 2,
          width: 20,
          height: 20,
          borderRadius: radius.sm,
          border: `2px solid ${item.completed ? colors.primary : colors.border}`,
          background: item.completed ? colors.primary : 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 150ms',
          padding: 0,
        }}
      >
        {item.completed && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path d="M1 4.5L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: typography.fontSizes.sm,
          color: item.completed ? colors.textMuted : colors.textPrimary,
          fontWeight: typography.fontWeights.medium,
          textDecoration: item.completed ? 'line-through' : 'none',
          lineHeight: 1.4,
        }}>
          {item.item}
        </div>
        {item.clause && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
            {item.clause}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressRing({ completed, total, size = 56 }) {
  const pct = total > 0 ? completed / total : 0;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors.border} strokeWidth={5} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke={pct === 1 ? '#10b981' : colors.primary}
          strokeWidth={5}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 400ms ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '11px', fontWeight: 700, color: colors.textPrimary,
      }}>
        {Math.round(pct * 100)}%
      </div>
    </div>
  );
}

function SessionDrawer({ session: initialSession, onClose, onRefresh }) {
  const [session, setSession] = useState(initialSession);
  const [activeTab, setActiveTab] = useState('checklist');
  const [acting, setActing] = useState(false);
  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const checklist = session.checklist || [];
  const completedCount = checklist.filter(i => i.completed).length;

  const handleChecklistUpdate = (index, updated) => {
    setSession(s => {
      const cl = [...(s.checklist || [])];
      cl[index] = updated;
      return { ...s, checklist: cl };
    });
  };

  const doAction = async (action) => {
    setActing(true);
    try {
      const res = await axiosClient.post(`/remote-audit/${session.id}/${action}`);
      setSession(res.data);
      onRefresh();
      flash(action === 'start' ? 'Session started.' : 'Session completed.');
    } catch (err) {
      flash(err?.response?.data?.detail || 'Action failed.', 'error');
    } finally {
      setActing(false);
    }
  };

  const TABS = [
    { key: 'checklist', label: 'Checklist' },
    { key: 'details', label: 'Details' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }}
      />
      <div style={{
        position: 'relative', width: 600, maxWidth: '100vw',
        background: colors.surface, height: '100%',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
        overflowY: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: `${spacing.lg} ${spacing.xl}`,
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.sm }}>
            <div>
              <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 4 }}>
                Remote Audit
              </div>
              <div style={{ fontSize: typography.fontSizes.xl, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, lineHeight: 1.2 }}>
                {session.company_name || `Session #${session.id}`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: radius.pill,
                fontSize: typography.fontSizes.xs,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                background: `${STATUS_COLORS[session.status]}20`,
                color: STATUS_COLORS[session.status],
              }}>
                {session.status?.replace('_', ' ')}
              </span>
              <button
                onClick={onClose}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: 22, lineHeight: 1, padding: 4 }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Progress summary */}
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg, marginTop: spacing.md }}>
            <ProgressRing completed={completedCount} total={checklist.length} />
            <div>
              <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>
                {completedCount} of {checklist.length} items complete
              </div>
              <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
                {session.scheduled_date ? `Scheduled: ${new Date(session.scheduled_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}` : 'No date set'}
              </div>
            </div>
            {session.meeting_link && (
              <a
                href={session.meeting_link}
                target="_blank"
                rel="noreferrer"
                style={{
                  marginLeft: 'auto',
                  padding: '8px 16px',
                  borderRadius: radius.md,
                  background: '#1e40af',
                  color: '#fff',
                  fontSize: typography.fontSizes.sm,
                  fontWeight: typography.fontWeights.semibold,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Join Meeting
              </a>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: `${spacing.sm} ${spacing.lg}`,
                border: 'none',
                borderBottom: `2px solid ${activeTab === t.key ? colors.primary : 'transparent'}`,
                background: 'none',
                cursor: 'pointer',
                fontSize: typography.fontSizes.sm,
                fontWeight: activeTab === t.key ? typography.fontWeights.semibold : typography.fontWeights.medium,
                color: activeTab === t.key ? colors.primary : colors.textMuted,
                transition: 'all 120ms',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: `${spacing.lg} ${spacing.xl}` }}>
          {msg && <Alert type={msg.type} message={msg.text} />}

          {activeTab === 'checklist' && (
            <div>
              {session.status === 'scheduled' && (
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: radius.md,
                  padding: spacing.md,
                  marginBottom: spacing.lg,
                  fontSize: typography.fontSizes.sm,
                  color: '#1e40af',
                }}>
                  Start the session to begin ticking off checklist items.
                </div>
              )}
              {checklist.length === 0 ? (
                <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.textMuted }}>
                  No checklist items.
                </div>
              ) : (
                checklist.map((item, i) => (
                  <CheckItem
                    key={i}
                    item={item}
                    index={i}
                    sessionId={session.id}
                    onUpdate={handleChecklistUpdate}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
              {[
                ['Standard', session.standard],
                ['Audit Scope', session.scope],
                ['Lead Auditor', session.auditor_name || '—'],
                ['Scheduled Date', session.scheduled_date ? new Date(session.scheduled_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'],
                ['Duration', session.duration_hours ? `${session.duration_hours} hour${session.duration_hours !== 1 ? 's' : ''}` : '—'],
                ['Meeting Link', session.meeting_link || '—'],
                ['Notes', session.notes || '—'],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 4 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary, lineHeight: 1.5 }}>
                    {label === 'Meeting Link' && session.meeting_link ? (
                      <a href={session.meeting_link} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                        {session.meeting_link}
                      </a>
                    ) : value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {(session.status === 'scheduled' || session.status === 'in_progress') && (
          <div style={{
            padding: `${spacing.md} ${spacing.xl}`,
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            gap: spacing.sm,
            flexShrink: 0,
            background: colors.surface,
          }}>
            {session.status === 'scheduled' && (
              <Btn variant="primary" size="md" onClick={() => doAction('start')} loading={acting}>
                Start Session
              </Btn>
            )}
            {session.status === 'in_progress' && (
              <Btn
                variant="success"
                size="md"
                onClick={() => doAction('complete')}
                loading={acting}
                disabled={completedCount < checklist.length}
              >
                Complete Session {completedCount < checklist.length ? `(${checklist.length - completedCount} remaining)` : ''}
              </Btn>
            )}
            <Btn variant="ghost" size="md" onClick={onClose}>Close</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    company_id: '',
    standard: '',
    scope: '',
    scheduled_date: '',
    duration_hours: 4,
    meeting_link: '',
    notes: '',
  });
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axiosClient.get('/companies/').then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await axiosClient.post('/remote-audit/', form);
      onCreated(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not schedule session.');
      setSaving(false);
    }
  };

  const FieldLabel = ({ children }) => (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}
    </div>
  );

  return (
    <StandardModal
      title="Schedule Remote Audit"
      onClose={onClose}
      width={560}
      footer={
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" size="md" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" size="md" type="submit" form="schedule-form" loading={saving}>Schedule Session</Btn>
        </div>
      }
    >
      {error && <Alert type="error" message={error} />}
      <form id="schedule-form" onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div>
            <FieldLabel>Certification Body</FieldLabel>
            <select style={selectStyle} value={form.company_id} onChange={e => set('company_id', e.target.value)} required>
              <option value="">Select company…</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
            <div>
              <FieldLabel>Standard</FieldLabel>
              <input style={inputStyle} value={form.standard} onChange={e => set('standard', e.target.value)} placeholder="ISO 17021-1" required />
            </div>
            <div>
              <FieldLabel>Duration (hours)</FieldLabel>
              <input style={inputStyle} type="number" min="0.5" max="16" step="0.5" value={form.duration_hours} onChange={e => set('duration_hours', parseFloat(e.target.value))} />
            </div>
          </div>

          <div>
            <FieldLabel>Audit Scope</FieldLabel>
            <input style={inputStyle} value={form.scope} onChange={e => set('scope', e.target.value)} placeholder="e.g. Management system certification — quality" required />
          </div>

          <div>
            <FieldLabel>Scheduled Date &amp; Time</FieldLabel>
            <input style={inputStyle} type="datetime-local" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} required />
          </div>

          <div>
            <FieldLabel>Meeting Link</FieldLabel>
            <input style={inputStyle} type="url" value={form.meeting_link} onChange={e => set('meeting_link', e.target.value)} placeholder="https://meet.google.com/…" />
          </div>

          <div>
            <FieldLabel>Notes</FieldLabel>
            <textarea style={{ ...textareaStyle, minHeight: 80 }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any pre-audit preparation notes…" />
          </div>
        </div>
      </form>
    </StandardModal>
  );
}

export default function RemoteAuditSupport() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axiosClient.get(`/remote-audit/${params}`);
      setSessions(res.data || []);
    } catch {
      flash('Could not load sessions.', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const kpis = [
    {
      label: 'Scheduled',
      value: sessions.filter(s => s.status === 'scheduled').length,
      color: colors.primary,
    },
    {
      label: 'In Progress',
      value: sessions.filter(s => s.status === 'in_progress').length,
      color: '#f59e0b',
    },
    {
      label: 'Completed',
      value: sessions.filter(s => s.status === 'completed').length,
      color: '#10b981',
    },
    {
      label: 'This Month',
      value: sessions.filter(s => {
        if (!s.scheduled_date) return false;
        const d = new Date(s.scheduled_date);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length,
      color: '#8b5cf6',
    },
  ];

  const columns = [
    {
      key: 'company_name',
      label: 'Organisation',
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>{v || '—'}</div>
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>{row.standard || ''}</div>
        </div>
      ),
    },
    {
      key: 'scope',
      label: 'Scope',
      render: v => (
        <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {v || '—'}
        </div>
      ),
    },
    {
      key: 'scheduled_date',
      label: 'Scheduled',
      render: v => v ? (
        <div>
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>
            {new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
            {new Date(v).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      ) : <span style={{ color: colors.textMuted }}>—</span>,
    },
    {
      key: 'checklist',
      label: 'Progress',
      render: (v) => {
        const total = (v || []).length;
        const done = (v || []).filter(i => i.completed).length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <div style={{ flex: 1, height: 5, background: colors.border, borderRadius: 99, minWidth: 80 }}>
              <div style={{
                height: '100%',
                width: `${pct}%`,
                background: pct === 100 ? '#10b981' : colors.primary,
                borderRadius: 99,
                transition: 'width 300ms ease',
              }} />
            </div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, flexShrink: 0 }}>
              {done}/{total}
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: v => (
        <span style={{
          padding: '3px 10px',
          borderRadius: radius.pill,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          background: `${STATUS_COLORS[v] || colors.border}20`,
          color: STATUS_COLORS[v] || colors.textMuted,
        }}>
          {v?.replace('_', ' ') || '—'}
        </span>
      ),
    },
    {
      key: 'id',
      label: '',
      render: (_, row) => (
        <Btn variant="ghost" size="sm" onClick={() => setSelectedSession(row)}>
          Open
        </Btn>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="Remote Audit Support"
        subtitle="Schedule and manage remote audit sessions with live checklist tracking"
        actions={
          <Btn variant="primary" size="md" onClick={() => setShowSchedule(true)}>
            Schedule Session
          </Btn>
        }
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      <KPIGrid kpis={kpis} columns={4} />

      <Card style={{ marginTop: spacing.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg, flexWrap: 'wrap', gap: spacing.sm }}>
          <FilterPills
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
        <DataTable
          columns={columns}
          rows={sessions}
          loading={loading}
          keyField="id"
          emptyMessage="No audit sessions found."
        />
      </Card>

      {selectedSession && (
        <SessionDrawer
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onRefresh={load}
        />
      )}

      {showSchedule && (
        <ScheduleModal
          onClose={() => setShowSchedule(false)}
          onCreated={(s) => {
            setShowSchedule(false);
            load();
            flash(`Session scheduled for ${s.company_name || 'organisation'}.`);
          }}
        />
      )}
    </div>
  );
}
